import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Color } from "../../../../../../../../components/Form/Button";
import { ModalType } from "../../../../../../../../components/Generic/Modal";
import { Cronjob } from "../../../../../../../../models/cronjob";
import { ConfirmationModal, ErrorMessage, ItalicErrorMessage } from "./styles";

export interface DeleteConfirmationModalHandles {
  open: (cronjob: Cronjob) => void;
  close: () => void;
}

interface ModalProps {
  closeModal?: () => void;
  onSubmit: (id: number) => void;
  loadingAction?: boolean;
}

const DeleteConfirmationModal: ForwardRefRenderFunction<
  DeleteConfirmationModalHandles,
  ModalProps
> = (props, ref) => {
  const { t: translate } = useTranslation();
  const { closeModal, onSubmit, loadingAction } = props;

  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [cronjob, setCronjob] = useState<Cronjob>({} as Cronjob);

  const open = useCallback((cronjob: Cronjob) => {
    setCronjob(cronjob);
    setIsOpened(true);
  }, []);

  const close = useCallback(() => {
    setIsOpened(false);
    setCronjob({} as Cronjob);
    if (closeModal) closeModal();
  }, []);

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const handleDelete = () => {
    if (!cronjob?.id) return;

    onSubmit(cronjob.id);
  };

  return (
    <ConfirmationModal
      data-cy="delete-cronjob-confirmation-modal"
      open={isOpened}
      type={ModalType.DELETE}
      handleClose={close}
      title={translate("deleteAutomation")}
      showActionButtons
      actionButtonProps={{
        onCancel: close,
        textSave: translate("delete"),
        loading: loadingAction,
        onSave: handleDelete,
        cancelDisabled: loadingAction,
        customColorCancel: Color.TEXT_INFO,
        customColorSave: Color.ERROR,
      }}
    >
      <Grid
        container
        spacing={2}
        className="mt-2"
        alignItems="flex-start"
        justifyContent="flex-start"
        direction="column"
      >
        <Grid item>
          <ErrorMessage>
            {`${translate("doYouReallyWantToDeleteTheAutomation")}${
              cronjob?.name ? ` ${cronjob.name}?` : "?"
            }`}
          </ErrorMessage>
        </Grid>
        <Grid item>
          <ItalicErrorMessage className="error">
            {translate("thisActionIsPermanentAndCannotBeUndone")}
          </ItalicErrorMessage>
        </Grid>
      </Grid>
    </ConfirmationModal>
  );
};

export default forwardRef(DeleteConfirmationModal);
